import {
  AppShell,
  Container,
  Box,
  useMantineTheme,
  ColorScheme,
} from "@mantine/core";
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { useIsSmallScreen, useIsSmallerThanLG } from "../Components/Utils";
import { useGlobalStatusContext } from "../GlobalStore";
import StateFromURL from "../StateFromURL";
import { EpPageProvider } from "../Components/EpPage";
import { PageLoadingFallback } from "../Components/LoadingFallback";
import PageTracking from "../Components/PageTracking";
import { ROUTER_BASE } from "../Components/Link";
import ScreenshotFlag from "../Components/ScreenshotFlag";
import HeaderSkeleton from "../ArbitrageScanSkeleton/HeaderSkeleton";
import Footer from "../Footer";
import ContainerRouter from "./ContainerRouter";

const NAVBAR_EXPAND_WIDTH = 260;
const NAVBAR_COLLAPSE_WIDTH = 60;

function MainRouter({ colorScheme }: { colorScheme: ColorScheme }) {
  const isSmallScreen = useIsSmallScreen();
  const isSmallerThanLG = useIsSmallerThanLG();

  const theme = useMantineTheme();

  // const Footer = React.lazy(
  //   () => import(/* webpackChunkName: "Footer" */ "../Footer")
  // );
  // const ContainerRouter = React.lazy(
  //   () => import(/* webpackChunkName: "ContainerRouter" */ "./ContainerRouter")
  // );
  const Navbar = React.lazy(
    () => import(/* webpackChunkName: "Navbar" */ "../Navbar")
  );
  const Header = React.lazy(
    () => import(/* webpackChunkName: "Header" */ "../Header")
  );
  const HighchartOption = React.lazy(
    () =>
      import(
        /* webpackChunkName: "HighchartOption" */ "../Components/Highcharts"
      )
  );
  const ScrollToTop = React.lazy(
    () =>
      import(/* webpackChunkName: "ScrollToTop" */ "../Components/ScrollToTop")
  );
  const BackTop = React.lazy(
    () => import(/* webpackChunkName: "BackTop" */ "../Components/BackTop")
  );
  const SandwichSearchPage = React.lazy(
    () =>
      import(
        /* webpackChunkName: "SandwichSearchPage" */ "../Sandwich/SearchPage"
      )
  );
  const EigenTxChartTweetPic = React.lazy(
    () =>
      import(
        /* webpackChunkName: "EigenTxChartTweetPic" */ "../EigenTx/TxChartTweetPic"
      )
  );

  const { navExpand, navVisibleOnSmallScreen, chain } =
    useGlobalStatusContext();
  const isNavExpand = navExpand !== undefined ? navExpand : !isSmallerThanLG;
  const isNavVisible =
    !isSmallScreen || (isSmallScreen && navVisibleOnSmallScreen);

  // const resize = () => {
  //   if (navExpand !== undefined && !isSmallScreen) {
  //     setNavExpand(undefined);
  //   }
  // };
  // 这里引起手机端 safari chrome 滚动刷新
  // useEffect(() => {
  //   window.addEventListener("resize", resize);
  //   return () => {
  //     window.removeEventListener("resize", resize);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const left = isNavVisible
    ? isNavExpand
      ? NAVBAR_EXPAND_WIDTH
      : NAVBAR_COLLAPSE_WIDTH
    : 0;

  return (
    <Router>
      <StateFromURL />
      <ScreenshotFlag />
      <PageTracking />
      <Suspense fallback={<></>}>
        <HighchartOption colorScheme={colorScheme} theme={theme} />
      </Suspense>
      <Suspense fallback={<></>}>
        <ScrollToTop />
      </Suspense>
      <Suspense fallback={<></>}>{isSmallScreen ? null : <BackTop />}</Suspense>
      <Routes>
        <Route path={`${ROUTER_BASE}`} element={<Outlet />}>
          <Route
            path="sandwich/search"
            element={
              <Suspense fallback={<PageLoadingFallback />}>
                <EpPageProvider pageId="Sandwich Overview">
                  <SandwichSearchPage />
                </EpPageProvider>
              </Suspense>
            }
          />
          <Route
            path="search"
            element={
              <Suspense fallback={<PageLoadingFallback />}>
                <EpPageProvider>
                  <SandwichSearchPage />
                </EpPageProvider>
              </Suspense>
            }
          />
          <Route
            path="eigentx/tweetpic/:txhash"
            element={
              <Suspense fallback={<PageLoadingFallback />}>
                <EpPageProvider>
                  <EigenTxChartTweetPic />
                </EpPageProvider>
              </Suspense>
            }
          />
        </Route>
        <Route
          path="*"
          element={
            <AppShell
              fixed
              header={
                // <HeaderSkeleton />
                <Suspense fallback={<HeaderSkeleton />}>
                  <Header isExtend={isNavExpand} />
                </Suspense>
              }
              navbar={
                <Suspense fallback={<></>}>
                  {isNavVisible ? <Navbar isExtend={isNavExpand} /> : undefined}
                </Suspense>
              }
              sx={(theme) => ({
                main: {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[8]
                      : theme.colors.gray[0],
                  padding: 0,
                  overflow: "hidden",
                  // minWidth: isSmallScreen ? "" : 1366,
                },
              })}
            >
              <Box
                hidden={isSmallScreen && navVisibleOnSmallScreen}
                sx={(theme) => {
                  return {
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[8]
                        : theme.colors.gray[0],
                    position: "relative",
                    zIndex: 10,
                    marginTop: 80,
                    marginLeft: left,
                    boxShadow:
                      "0 1px 3px rgb(0 0 0 / 5%), rgb(0 0 0 / 5%) 0px 10px 15px -5px, rgb(0 0 0 / 4%) 0px 7px 7px -5px",
                  };
                }}
              >
                <Container
                  id="arbitrage-container"
                  className="arbitrage-container"
                  fluid
                  sx={{
                    paddingTop: 24,
                    paddingBottom: 24,
                    minHeight: window.innerHeight - 230,
                  }}
                  px={isSmallScreen ? 0 : "xl"}
                >
                  {/* <Suspense
                    fallback={() => {
                      return "";
                    }}
                  >
                    <ContainerRouter />
                  </Suspense> */}
                  <ContainerRouter />
                </Container>
              </Box>
              {/* Footer里面有LCP元素，需要尽早处理 */}
              <Footer chain={chain} left={left} isSmallScreen={isSmallScreen} />
            </AppShell>
          }
        />
      </Routes>
    </Router>
  );
}

export default MainRouter;
